import { createContext, forwardRef, useContext } from 'react';
import { cn } from '@/lib/cn';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  /** 0 = bare (no surface, no border) · 1 = panel (default) · 2 = raised / nested popover. */
  elevation?: 0 | 1 | 2;
  padding?: CardPadding;
  /** Hover affordance for a card that is itself a link or button target. */
  interactive?: boolean;
};

const PADDING: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

/** Horizontal padding the compound parts apply when the Card itself is unpadded. */
const SELF_PADDING_X: Record<CardPadding, string> = {
  none: 'px-4',
  sm: 'px-0',
  md: 'px-0',
  lg: 'px-0',
};

const ELEVATION: Record<NonNullable<CardProps['elevation']>, string> = {
  // §5.3 — panels get an outline AND a surface step. Never a resting shadow in dark mode;
  // `shadow-sm` resolves to `none` there and to a 1px lift in light.
  0: 'bg-transparent',
  1: 'bg-surface-1 border border-border rounded-md shadow-sm',
  2: 'bg-surface-2 border border-border-subtle rounded-md',
};

const CardPaddingContext = createContext<CardPadding>('md');

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { elevation = 1, padding = 'md', interactive = false, className, children, ...rest },
  ref,
) {
  return (
    <CardPaddingContext.Provider value={padding}>
      <div
        ref={ref}
        className={cn(
          'ui-card',
          ELEVATION[elevation],
          PADDING[padding],
          interactive &&
            cn(
              'cursor-pointer transition-colors duration-fast ease-out',
              'hover:bg-wash-hover hover:border-border-strong',
              'focus-visible:outline-none focus-visible:shadow-focus',
            ),
          className,
        )}
        {...rest}
        data-elevation={elevation}
      >
        {children}
      </div>
    </CardPaddingContext.Provider>
  );
}) as React.ForwardRefExoticComponent<CardProps & React.RefAttributes<HTMLDivElement>> & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};

export type CardHeaderProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  /** §5.3 — a divider is drawn only when the body is a table. */
  divided?: boolean;
  className?: string;
};

function CardHeader({
  title,
  subtitle,
  actions,
  divided = false,
  className,
}: CardHeaderProps): JSX.Element {
  const padding = useContext(CardPaddingContext);
  return (
    <div
      className={cn(
        'ui-card-header flex items-start justify-between gap-3 pb-3',
        SELF_PADDING_X[padding],
        padding === 'none' && 'pt-3',
        divided && 'border-b border-border-subtle',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="truncate text-heading text-ink">{title}</h2>
        {subtitle != null && <p className="mt-0.5 text-caption text-ink-tertiary">{subtitle}</p>}
      </div>
      {actions != null && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
    </div>
  );
}

export type CardBodyProps = React.HTMLAttributes<HTMLDivElement>;

function CardBody({ className, children, ...rest }: CardBodyProps): JSX.Element {
  const padding = useContext(CardPaddingContext);
  return (
    <div className={cn(SELF_PADDING_X[padding], className)} {...rest}>
      {children}
    </div>
  );
}

export type CardFooterProps = React.HTMLAttributes<HTMLDivElement>;

function CardFooter({ className, children, ...rest }: CardFooterProps): JSX.Element {
  const padding = useContext(CardPaddingContext);
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 pt-3',
        SELF_PADDING_X[padding],
        padding === 'none' && 'pb-3',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export { CardHeader, CardBody, CardFooter };
